/**
 * IntegrationsPage — the five external connectors.
 *
 * One card per connector (file_upload, google_drive, gmail,
 * google_calendar, stripe). Cards expose the four CONNECTORS-SPEC
 * operations: connect, disconnect, list_items, create_proof.
 *
 * Auth flow:
 *   - OAuth (Google × 3): connect → server returns `authorize_url`,
 *     we set a `pending` marker in localStorage and redirect the
 *     browser. Google → SPA `/oauth/callback/:slug` → completeOAuth
 *     → handle stored in localStorage.
 *   - API key (Stripe): inline modal collects `sk_…`, sends in body.
 *   - Public (FileUpload): no connect step; the upload action is the
 *     proof creator.
 */
import { useEffect, useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';

import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Spinner } from '@/components/LoadingState';
import { useToast } from '@/components/Toaster';
import {
  completeOAuth as _completeOAuth,
  connectConnector,
  createConnectorProof,
  createFileUploadProof,
  disconnectConnector,
  listConnectorItems,
  listConnectors,
  type ConnectionHandle,
  type ConnectorItem,
  type ConnectorRow,
  type ConnectorSlug,
  type CreateProofResponse,
} from '@/api/external-connectors';
import {
  clearHandle,
  loadHandle,
  saveHandle,
  setOAuthPending,
} from './handle-storage';

const ICONS: Record<ConnectorSlug, string> = {
  file_upload: '📄',
  google_drive: '📂',
  gmail: '✉️',
  google_calendar: '📅',
  stripe: '💳',
};

// ─── Additional connectors (preview surface) ─────────────────────
//
// These cards are rendered statically below the API-driven grid for
// connectors the engine team has built or scoped but not yet exposed
// through `listConnectors()`. The cards convey the product range to
// users; the Connect action shows a toast acknowledging that engine
// wiring lands next release.
//
// Promotion path: as each connector ships in `gi-connectors` and
// surfaces from `/api/v1/connectors`, drop its entry from this list
// (or it'll render twice, once dynamic + once static).

interface ExtraConnectorRow {
  id: string;
  name: string;
  icon: string;
  description: string;
  version: string;
  /** Auth label shown under the name, parallel to `oauth2 / api_key / public`. */
  auth_label: string;
  /** Available = built but pending API exposure. Coming Soon = not built. */
  status: 'available' | 'coming_soon';
}

const EXTRA_CONNECTORS: readonly ExtraConnectorRow[] = [
  {
    id: 'webhook',
    name: 'Webhook',
    icon: '🔗',
    description: 'Receive and send signed webhooks.',
    version: '0.1.0',
    auth_label: 'Shared secret',
    status: 'available',
  },
  {
    id: 'email_smtp',
    name: 'Email / SMTP',
    icon: '📧',
    description: 'Send signed emails, receive complaints.',
    version: '0.1.0',
    auth_label: 'SMTP credentials',
    status: 'available',
  },
  {
    id: 'scheduler',
    name: 'Scheduler',
    icon: '⏰',
    description: 'Run signed tasks on schedule.',
    version: '0.1.0',
    auth_label: 'No auth',
    status: 'available',
  },
  {
    id: 'google_sheets',
    name: 'Google Sheets',
    icon: '📊',
    description: 'Read/write signed spreadsheet data.',
    version: '0.1.0',
    auth_label: 'OAuth',
    status: 'available',
  },
  {
    id: 'github',
    name: 'GitHub',
    icon: '🐙',
    description: 'Sign commits and releases.',
    version: '0.1.0',
    auth_label: 'Personal Access Token',
    status: 'coming_soon',
  },
  {
    id: 'notion',
    name: 'Notion',
    icon: '📝',
    description: 'Sign ideas and documents.',
    version: '0.1.0',
    auth_label: 'Bearer token',
    status: 'coming_soon',
  },
  {
    id: 'tiktok',
    name: 'TikTok',
    icon: '🎵',
    description: 'Prove original video content.',
    version: '0.1.0',
    auth_label: 'OAuth',
    status: 'coming_soon',
  },
  {
    id: 'instagram',
    name: 'Instagram',
    icon: '📸',
    description: 'Prove original photo/reel content.',
    version: '0.1.0',
    auth_label: 'Meta OAuth',
    status: 'coming_soon',
  },
];

export function IntegrationsPage() {
  const [catalogue, setCatalogue] = useState<ConnectorRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    listConnectors()
      .then((res) => {
        if (cancelled) return;
        setCatalogue(res.connectors);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [reloadTick]);

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-ink">Integrations</h1>
        <p className="mt-1 text-sm text-ink-muted">
          Connect external accounts so a GI can pull signed proofs straight from
          the source. Tokens stay in the engine&rsquo;s out-of-band store; only
          a hashed handle is mirrored to your browser.
        </p>
      </header>

      {error ? (
        <Card>
          <p className="text-sm text-danger">Could not load connectors: {error}</p>
          <Button variant="ghost" size="sm" onClick={() => setReloadTick((n) => n + 1)} className="mt-3">
            Retry
          </Button>
        </Card>
      ) : null}

      {!catalogue && !error ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="md" />
        </div>
      ) : null}

      {catalogue ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {catalogue.map((row) => (
            <IntegrationCard key={row.id} row={row} onChange={() => setReloadTick((n) => n + 1)} />
          ))}
        </div>
      ) : null}

      {/* ── Additional connectors (preview) ─────────────────────────
          Built or scoped by the engine team but not yet exposed by
          `listConnectors()`. Promotion to the live grid above happens
          as each lands in `/api/v1/connectors`. */}
      <section aria-labelledby="extras-heading" className="mt-4">
        <div className="mb-3 flex items-baseline justify-between gap-4 border-b border-border pb-2">
          <h2 id="extras-heading" className="text-base font-semibold tracking-tight text-ink">
            Additional connectors
          </h2>
          <span className="text-xs text-ink-muted">
            {EXTRA_CONNECTORS.filter((r) => r.status === 'available').length} available · {EXTRA_CONNECTORS.filter((r) => r.status === 'coming_soon').length} coming soon
          </span>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {EXTRA_CONNECTORS.map((row) => (
            <ExtraIntegrationCard key={row.id} row={row} />
          ))}
        </div>
      </section>
    </div>
  );
}

function ExtraIntegrationCard({ row }: { row: ExtraConnectorRow }) {
  const toast = useToast();
  const isAvailable = row.status === 'available';

  function onConnect() {
    // Engine doesn't yet expose this connector via `listConnectors()`,
    // so a real connect call would 404. Surface that honestly via toast
    // rather than firing a request that will fail.
    toast.success(
      `${row.name} connector`,
      'Engine wiring lands in the next release. The connector is built; the dashboard surface is being staged.',
    );
  }

  return (
    <Card className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="text-2xl" aria-hidden>{row.icon}</span>
          <div>
            <div className="text-sm font-semibold text-ink">{row.name}</div>
            <div className="text-xs text-ink-muted">
              {row.auth_label}
              {' · '}v{row.version}
            </div>
          </div>
        </div>
        <span
          className={
            isAvailable
              ? 'inline-flex items-center rounded-full bg-success/10 px-2 py-0.5 text-[11px] font-medium text-success'
              : 'inline-flex items-center rounded-full bg-ink-muted/10 px-2 py-0.5 text-[11px] font-medium text-ink-muted'
          }
        >
          {isAvailable ? 'Available' : 'Coming Soon'}
        </span>
      </div>

      <p className="text-xs text-ink-muted">{row.description}</p>

      <div className="mt-auto flex flex-wrap gap-2">
        {isAvailable ? (
          <Button variant="primary" size="sm" onClick={onConnect}>
            Connect
          </Button>
        ) : (
          <Button variant="secondary" size="sm" disabled>
            Coming Soon
          </Button>
        )}
      </div>
    </Card>
  );
}

interface IntegrationCardProps {
  row: ConnectorRow;
  onChange: () => void;
}

function IntegrationCard({ row, onChange }: IntegrationCardProps) {
  const toast = useToast();
  const [handle, setHandle] = useState<ConnectionHandle | null>(() => loadHandle(row.id));
  const [busy, setBusy] = useState<null | 'connect' | 'disconnect' | 'browse'>(null);
  const [stripeOpen, setStripeOpen] = useState(false);
  const [browseOpen, setBrowseOpen] = useState(false);

  const isConnected = useMemo(
    () => handle?.kind === 'connected',
    [handle],
  );
  const userHandle = handle?.kind === 'connected' ? handle.user_handle : null;

  async function startOAuth() {
    setBusy('connect');
    try {
      const res = await connectConnector(row.id);
      if (res.handle.kind !== 'oauth_redirect') {
        toast.error('Unexpected response', 'Server returned a non-OAuth handle');
        return;
      }
      setOAuthPending(row.id, res.handle.state);
      window.location.href = res.handle.authorize_url;
    } catch (e: unknown) {
      toast.error(
        'Could not start OAuth',
        e instanceof Error ? e.message : String(e),
      );
    } finally {
      setBusy(null);
    }
  }

  async function connectStripe(apiKey: string) {
    setBusy('connect');
    try {
      const res = await connectConnector('stripe', { api_key: apiKey });
      if (res.handle.kind !== 'connected') {
        toast.error('Unexpected response', 'Stripe did not return a connected handle');
        return;
      }
      saveHandle('stripe', res.handle);
      setHandle(res.handle);
      setStripeOpen(false);
      toast.success('Stripe connected');
      onChange();
    } catch (e: unknown) {
      toast.error('Stripe rejected the key', e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function disconnect() {
    if (!handle || handle.kind !== 'connected') return;
    setBusy('disconnect');
    try {
      await disconnectConnector(row.id, handle);
      clearHandle(row.id);
      setHandle(null);
      toast.success(`${row.display_name} disconnected`);
      onChange();
    } catch (e: unknown) {
      toast.error('Disconnect failed', e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  function onConnectClick() {
    if (row.auth_kind === 'oauth2') {
      void startOAuth();
    } else if (row.auth_kind === 'api_key') {
      setStripeOpen(true);
    } else if (row.id === 'file_upload') {
      // FileUpload's "connect" is implicit; jump directly to upload.
      void uploadFileFlow(row, toast.error, toast.success);
    }
  }

  return (
    <Card className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="text-2xl" aria-hidden>{ICONS[row.id]}</span>
          <div>
            <div className="text-sm font-semibold text-ink">{row.display_name}</div>
            <div className="text-xs text-ink-muted">
              {row.auth_kind === 'oauth2' ? 'OAuth' : row.auth_kind === 'api_key' ? 'API key' : 'No auth'}
              {' · '}v{row.version}
            </div>
          </div>
        </div>
        <StatusPill connected={isConnected} />
      </div>

      {isConnected && userHandle ? (
        <p className="text-xs text-ink-muted">
          Connected as <code className="rounded bg-surface-raised px-1">{userHandle}</code>
        </p>
      ) : null}

      <div className="mt-auto flex flex-wrap gap-2">
        {row.id === 'file_upload' ? (
          <Button
            variant="primary"
            size="sm"
            onClick={() => uploadFileFlow(row, toast.error, toast.success)}
          >
            Upload &amp; sign
          </Button>
        ) : !isConnected ? (
          <Button variant="primary" size="sm" onClick={onConnectClick} disabled={busy === 'connect'}>
            {busy === 'connect' ? 'Connecting…' : 'Connect'}
          </Button>
        ) : (
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setBrowseOpen(true)}
              disabled={busy !== null}
            >
              Browse &amp; prove
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => void disconnect()}
              disabled={busy === 'disconnect'}
            >
              {busy === 'disconnect' ? 'Disconnecting…' : 'Disconnect'}
            </Button>
          </>
        )}
      </div>

      {stripeOpen ? (
        <StripeKeyDialog
          open={stripeOpen}
          onOpenChange={setStripeOpen}
          onSubmit={connectStripe}
          submitting={busy === 'connect'}
        />
      ) : null}

      {browseOpen && handle?.kind === 'connected' ? (
        <BrowseItemsDialog
          open={browseOpen}
          onOpenChange={setBrowseOpen}
          slug={row.id}
          handle={handle}
        />
      ) : null}
    </Card>
  );
}

function StatusPill({ connected }: { connected: boolean }) {
  return (
    <span
      className={
        connected
          ? 'rounded-full bg-success-soft px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-success'
          : 'rounded-full bg-surface-raised px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-ink-subtle'
      }
    >
      {connected ? 'Connected' : 'Not connected'}
    </span>
  );
}

interface StripeKeyDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSubmit: (key: string) => void | Promise<void>;
  submitting: boolean;
}

function StripeKeyDialog({ open, onOpenChange, onSubmit, submitting }: StripeKeyDialogProps) {
  const [apiKey, setApiKey] = useState('');
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        {/* Viewport-relative centering: `left-1/2 -translate-x-1/2` is
            screen-space geometry, not text flow. Allowlisted in rtl.spec.ts. */}
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-[min(28rem,90vw)] -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-surface p-6 shadow-raised"
          aria-describedby={undefined}
        >
          <Dialog.Title className="text-base font-semibold text-ink">Connect Stripe</Dialog.Title>
          <p className="mt-1 text-xs text-ink-muted">
            Paste your secret key (starts with <code>sk_live_</code> or
            <code>sk_test_</code>). The server validates it against
            <code className="mx-1">/v1/account</code> before storing.
          </p>
          <input
            type="password"
            placeholder="sk_live_…"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            className="mt-4 w-full rounded-md border border-border bg-surface px-3 py-2 text-sm font-mono text-ink"
          />
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => void onSubmit(apiKey.trim())}
              disabled={submitting || apiKey.trim().length === 0}
            >
              {submitting ? 'Validating…' : 'Connect'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

interface BrowseItemsDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  slug: ConnectorSlug;
  handle: ConnectionHandle;
}

function BrowseItemsDialog({ open, onOpenChange, slug, handle }: BrowseItemsDialogProps) {
  const toast = useToast();
  const userHandle = handle.kind === 'connected' ? handle.user_handle : '';
  const [items, setItems] = useState<ConnectorItem[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [provingId, setProvingId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setItems(null);
    setLoadError(null);
    listConnectorItems(slug, userHandle, { limit: 25 })
      .then((rows) => {
        if (cancelled) return;
        setItems(rows);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setLoadError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [open, slug, userHandle]);

  async function proveItem(itemId: string) {
    setProvingId(itemId);
    try {
      const res = await createConnectorProof(slug, { handle, item_id: itemId });
      announceProof(res, toast.success);
    } catch (e: unknown) {
      toast.error('Proof failed', e instanceof Error ? e.message : String(e));
    } finally {
      setProvingId(null);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        {/* Viewport-relative centering: `left-1/2 -translate-x-1/2` is
            screen-space geometry, not text flow. Allowlisted in rtl.spec.ts. */}
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[80vh] w-[min(40rem,92vw)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-lg border border-border bg-surface shadow-raised"
          aria-describedby={undefined}
        >
          <div className="border-b border-border p-4">
            <Dialog.Title className="text-base font-semibold text-ink">Pick an item to prove</Dialog.Title>
            <p className="text-xs text-ink-muted">
              The connector pulls the item from {labelFor(slug)} and signs the
              proof envelope under the engine identity.
            </p>
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            {loadError ? (
              <p className="text-sm text-danger">{loadError}</p>
            ) : !items ? (
              <div className="flex items-center justify-center py-8">
                <Spinner size="md" />
              </div>
            ) : items.length === 0 ? (
              <p className="text-sm text-ink-muted">No items returned.</p>
            ) : (
              <ul className="flex flex-col divide-y divide-border">
                {items.map((it) => (
                  <li key={it.id} className="flex items-center justify-between gap-3 py-2">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-ink">{it.display || it.id}</div>
                      <div className="truncate text-xs text-ink-muted">
                        <span className="font-mono">{it.id}</span>
                        {it.created_at ? ` · ${it.created_at}` : ''}
                      </div>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => void proveItem(it.id)}
                      disabled={provingId !== null}
                    >
                      {provingId === it.id ? 'Proving…' : 'Prove'}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex justify-end gap-2 border-t border-border p-4">
            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

async function uploadFileFlow(
  _row: ConnectorRow,
  onError: (title: string, body?: string) => void,
  onSuccess: (title: string, body?: string) => void,
) {
  const input = document.createElement('input');
  input.type = 'file';
  input.onchange = async () => {
    const file = input.files?.[0];
    if (!file) return;
    try {
      const res = await createFileUploadProof(file);
      announceProof(res, onSuccess);
    } catch (e: unknown) {
      onError('Upload failed', e instanceof Error ? e.message : String(e));
    }
  };
  input.click();
}

function announceProof(
  res: CreateProofResponse,
  onSuccess: (title: string, body?: string) => void,
) {
  const id = res.proof.proof_id;
  onSuccess('Proof signed', `proof_id ${id.slice(0, 12)}…`);
}

function labelFor(slug: ConnectorSlug): string {
  switch (slug) {
    case 'google_drive':
      return 'Google Drive';
    case 'gmail':
      return 'Gmail';
    case 'google_calendar':
      return 'Google Calendar';
    case 'stripe':
      return 'Stripe';
    case 'file_upload':
      return 'your local files';
  }
}
